import alumniApi from '@alumni_v2/api/alumni';
import { isUserLoggedIn } from '@alumni_v2/utils/helpers';
import { toast } from '@common/ui/general/Toast';
import { resetTurnstile } from '@common/ui/general/Turnstile';
import {
  getUpcomingLiveClass, eventRegisteration,
} from '@alumni_v2/api/freeClass';
import { QA_TURNSTILE_WIDGET_ID } from '@alumni_v2/utils/constants';
import { buildQueryParams } from '@alumni_v2/utils/buildQueryParams';
import ACTIONS from './types';
import { createQueryStringFromFilters } from '@alumni_v2/utils/createQuery';

const DEFAULT_ERROR_MESSAGE = 'Free Live class cannot be booked right'
  + ' now. Please try later';

export const fetchReviews = (
  pageNumber = 1, addMultipleParams = false,
) => async (
  dispatch, getState,
) => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const userSlug = urlParams.get('user');

    dispatch({
      type: ACTIONS.SET_REVIEWS_INIT,
      payload: { reset: pageNumber === 1 },
    });

    const queryParams = buildQueryParams(
      getState().reviews, userSlug, addMultipleParams,
    );
    const result = await alumniApi.fetchProfiles(
      queryParams, pageNumber, addMultipleParams,
    );
    if (result?.data?.data) {
      let data = result?.data?.data;
      if (userSlug) {
        data = [
          { ...data[0], forceOpen: true },
        ];
      }
      const currentTestimonials = getState().reviews?.data?.testimonials || [];
      const pagination = {
        currentPage: pageNumber,
        totalPages: Math.ceil(result.total_entries / 12),
        totalEntries: result.total_entries,
      };
      const payload = {
        testimonials: pageNumber === 1
          ? data : [...currentTestimonials, ...data],
        pagination,
      };
      dispatch({ type: ACTIONS.SET_REVIEWS_COMPLETE, payload });
    }
  } catch (error) {
    toast.show({
      type: 'error',
      message: error?.responseJson?.message || 'Oops! Something went wrong',
    });
    dispatch({
      type: ACTIONS.SET_REVIEWS_ERROR,
      error: 'Oops! Something went wrong',
    });
  }
};

export const openQnA = (askedToId) => (dispatch) => {
  dispatch({ type: ACTIONS.OPEN_QNA_MODAL, payload: askedToId });
};

export const closeQnA = () => (dispatch) => {
  dispatch({ type: ACTIONS.CLOSE_QNA_MODAL });
};

export const closeMissingDetails = () => (dispatch) => {
  dispatch({ type: ACTIONS.TOGGLE_MISSING_DETAILS });
};

export const getQuestions = (askedToId) => async (dispatch) => {
  try {
    dispatch({ type: ACTIONS.SET_QUESTIONS_INIT });

    const result = await alumniApi.getQuestions(askedToId);
    if (result?.data) {
      dispatch({
        type: ACTIONS.SET_QUESTIONS_COMPLETE,
        payload: { questions: result.data, askedToId, open: true },
      });
    }
  } catch (error) {
    const validError = error.response.status !== 401;
    if (validError) {
      toast.show({
        type: 'error',
        message: error?.responseJson?.message || 'Oops! Something went wrong',
      });
    }

    dispatch({
      type: ACTIONS.SET_QUESTIONS_COMPLETE,
      payload: { questions: [], askedToId, open: !validError },
    });
  }
};

export const getLiveClass = (program) => async (dispatch) => {
  try {
    const { data } = await getUpcomingLiveClass(program);
    if (data && data.attributes) {
      const {
        'registered_for_event?': registeredForEvent,
        id: freeClassId,
        slug: freeClassSlug,
      } = data.attributes;

      dispatch({
        type: ACTIONS.SET_UPCOMING_LIVE_CLASS,
        payload: { id: freeClassId, slug: freeClassSlug },
      });

      if (registeredForEvent) {
        dispatch(dispatch({ type: ACTIONS.SET_FC_REGISTERED_ALREADY }));
      }
    }
  } catch (error) {
    dispatch({ type: ACTIONS.SET_UPCOMING_LIVE_CLASS_ERROR });
  }
};

export const getInitialResources = () => async (dispatch) => {
  try {
    dispatch({ type: ACTIONS.SET_INITIAL_RESOURCES_INIT });

    const result = await alumniApi.initialLoad();
    if (result?.meta) {
      const {
        crt_completed: crtCompleted,
        set_completed: setCompleted,
        paid_user: paidUser,
        mentee_id: menteeId,
        product,
      } = result?.meta;

      dispatch({
        type: ACTIONS.SET_INITIAL_RESOURCES_COMPLETE,
        payload: {
          paidUser,
          setCompleted,
          crtCompleted,
          menteeId,
          product,
        },
      });

      dispatch(getLiveClass(product || 'academy'));
    }
  } catch (error) {
    const validError = error.response.status !== 401;
    if (validError) {
      toast.show({
        type: 'error',
        message: error?.responseJson?.message || 'Oops! Something went wrong',
      });
    }
    dispatch({
      type: ACTIONS.SET_INITIAL_RESOURCES_ERROR,
    });
  }
};


export const verifyQuestions = (
  askedToId, postVerificationCallback,
) => async (dispatch) => {
  try {
    const result = await alumniApi.verifyQuestions(askedToId);
    if (result?.data) {
      dispatch({
        type: ACTIONS.SET_USER_DETAILS,
        payload: {
          missingDetails: result.data.missing_details,
          userDetails: result.data.user_details,
          successModalOpen: !result.data.missing_details,
        },
      });
      if (result.data.missing_details) {
        postVerificationCallback(true, 'fill the missing details');
      } else {
        postVerificationCallback(true, 'success');
      }
    }
    dispatch({
      type: ACTIONS.SET_VERIFY_COMPLETE,
    });
    if (!result?.data) {
      toast.show({
        type: 'success',
        message: 'Submitted Successfully!',
      });
      postVerificationCallback(true, 'success');
    }
  } catch (error) {
    const message = error?.responseJson?.message
    || 'Oops! Something went wrong';
    toast.show({
      type: 'error',
      message,
    });
    postVerificationCallback(false, message);
    dispatch({
      type: ACTIONS.SET_VERIFY_COMPLETE,
    });
  }
};

export const deleteQuestion = (
  askedToId, questionId, recaptcha,
) => async (dispatch, getState) => {
  try {
    await alumniApi.deleteQuestion(questionId, recaptcha);
    if (isUserLoggedIn()) {
      dispatch(getQuestions(askedToId));
    } else {
      const currentQuestions = (
        getState().reviews?.questions || []
      ).filter(item => item.id !== questionId);
      dispatch({
        type: ACTIONS.SET_QUESTIONS_COMPLETE,
        payload: { questions: currentQuestions, askedToId, open: true },
      });
    }
  } catch (error) {
    toast.show({
      type: 'error',
      message: error?.responseJson?.message || 'Oops! Something went wrong',
    });
  } finally {
    resetTurnstile(QA_TURNSTILE_WIDGET_ID);
  }
};

export const updateQuestion = (
  askedToId, questionId, description, recaptcha,
) => async (dispatch, getState) => {
  try {
    const response = await alumniApi.updateQuestion(
      questionId, description, recaptcha,
    );
    if (isUserLoggedIn()) {
      dispatch(getQuestions(askedToId));
    } else if (response?.data) {
      const currentQuestions = (
        getState().reviews?.questions || []
      ).map(item => {
        if (item.id === questionId) {
          return response?.data;
        } else {
          return item;
        }
      });
      dispatch({
        type: ACTIONS.SET_QUESTIONS_COMPLETE,
        payload: { questions: currentQuestions, askedToId, open: true },
      });
    }
  } catch (error) {
    toast.show({
      type: 'error',
      message: error?.responseJson?.message || 'Oops! Something went wrong',
    });
  } finally {
    resetTurnstile(QA_TURNSTILE_WIDGET_ID);
  }
};

export const submitQuestion = (
  askedToId, description, recaptcha,
) => async (dispatch, getState) => {
  try {
    const response = await alumniApi.submitQuestion(
      askedToId, description, recaptcha,
    );
    if (isUserLoggedIn()) {
      dispatch(getQuestions(askedToId));
    } else {
      const currentQuestions = getState().reviews?.questions || [];
      if (response?.data) {
        currentQuestions.push(response?.data);
      }
      dispatch({
        type: ACTIONS.SET_QUESTIONS_COMPLETE,
        payload: { questions: currentQuestions, askedToId, open: true },
      });
    }
  } catch (error) {
    toast.show({
      type: 'error',
      message: error?.responseJson?.message || 'Oops! Something went wrong',
    });
  } finally {
    resetTurnstile(QA_TURNSTILE_WIDGET_ID);
  }
};

export const submitUserDetails = (
  data, postUserDetailsCallback,
) => async (dispatch) => {
  try {
    await alumniApi.submitUserDetails(data);
    dispatch({
      type: ACTIONS.SET_USER_DETAILS,
      payload: {
        missingDetails: false,
        userDetails: data,
        successModalOpen: true,
      },
    });
    postUserDetailsCallback(true, 'success');
  } catch (error) {
    const message = error?.responseJson?.message
    || 'Oops! Something went wrong';
    toast.show({
      type: 'error',
      message,
    });
    postUserDetailsCallback(false, message);
  }
};

export const updateRequestParams = (
  quickFilters = [], advancedFilters = {}, search = null,
  checkfirstTime = false,
  addMultipleParams = false,
) => (dispatch) => {
  let queryString = '';
  if (quickFilters.length > 0) {
    queryString = quickFilters
      .map(filter => `keywords=${encodeURIComponent(filter)}`).join('&');
  }

  let advancedQuery = '';
  advancedQuery = createQueryStringFromFilters(advancedFilters);
  if (!addMultipleParams) queryString += advancedQuery;
  else {
    queryString = queryString.length > 0
      ? `${queryString}&${advancedQuery}` : advancedQuery;
  }

  if (checkfirstTime) {
    const urlParams = new URLSearchParams(window.location.search);
    const shared = urlParams.get('shared');
    if (shared) {
      queryString += '&shared=true';
    }
  }

  const urlParams = new URLSearchParams(window.location.search);
  const alumniFilters = urlParams.get('alumni_id');

  if (alumniFilters) {
    queryString += `&alumni_id=${alumniFilters}`;
  }

  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;

  window.history.pushState({ path: newUrl }, '', newUrl);
  dispatch({
    type: ACTIONS.SET_REQUEST_PARAMS,
    payload: {
      quickFilters,
      advancedFilters,
      search,
      isShareable: true,
    },
  });
  dispatch(fetchReviews(1, addMultipleParams));
};

export const registerFcEvent = () => async (dispatch, getState) => {
  dispatch({ type: ACTIONS.SET_FC_REGISTERING });
  try {
    const {
      success, message = '',
    } = await eventRegisteration(getState().reviews.fcEventData.id);

    if (success) {
      dispatch({ type: ACTIONS.SET_FC_REGISTERED });
    } else {
      toast.show({
        type: 'error',
        message: message || DEFAULT_ERROR_MESSAGE,
      });
    }
  } catch (error) {
    toast.show({
      type: 'error',
      message: DEFAULT_ERROR_MESSAGE,
    });
  }
};

export const toggleShareState = () => (dispatch) => {
  dispatch({
    type: ACTIONS.TOGGLE_SHARE_STATE,
  });
};

export const toggleSuccessModal = () => (dispatch) => {
  dispatch({
    type: ACTIONS.TOGGLE_SUCCESS_MODAL,
  });
  dispatch(fetchReviews());
};

export const openFCModal = () => (dispatch) => {
  dispatch({ type: ACTIONS.FC_MODAL_OPEN });
};

export const closeFCModal = () => (dispatch) => {
  dispatch({ type: ACTIONS.FC_MODAL_CLOSE });
};

export const setFCRegistered = () => (dispatch) => {
  dispatch({ type: ACTIONS.SET_FC_REGISTERED });
};

export const setFCRegistering = () => (dispatch) => {
  dispatch({ type: ACTIONS.SET_FC_REGISTERING, payload: true });
};

export const setUpcomingLiveClass = (classData) => (dispatch) => {
  dispatch({ type: ACTIONS.SET_UPCOMING_LIVE_CLASS, payload: classData });
};

export const setIsPersonalisationModalOpen = (value) => (dispatch) => {
  dispatch({ type: ACTIONS.SET_PERSONALISATION_MODAL_OPEN, payload: value });
};

export const setIsProfileSidebarModalOpen = (value) => (dispatch) => {
  dispatch({ type: ACTIONS.SET_PROFILE_SIDEBAR, payload: value });
};

export const setIsSignupSidebarOpen = (value) => (dispatch) => {
  dispatch({ type: ACTIONS.SET_SIGNUP_SIDEBAR, payload: value });
};
